import { defineStore } from 'pinia';
import { ref, computed, shallowRef, markRaw } from 'vue';
import type { NotificationMessage, AttachedEmailRef, EmailAddress } from '@/types';
import type { EmailDraft } from '@/services/emailService';
import type { EmailTemplate, TemplateDataContext } from '@/types/emailTemplate';
import type { AttachmentSource } from '@/components/email/DocumentAttachmentPicker.vue';
import { useNotification } from '@/composables/useNotification';
import { i18n } from '@/config/i18n';

export type ComposerMode = 'new' | 'reply' | 'replyAll' | 'forward';
export type ComposerHost = 'inline' | 'floating';
export type ComposerWindowState = 'open' | 'minimized' | 'maximized';

/** Compositores abiertos a la vez, sumando el que está en línea (caso límite 12 de la spec). */
export const MAX_COMPOSER_SESSIONS = 3;

/**
 * Un compositor abierto: la bandeja conserva el suyo en línea, y el resto
 * vive flotante en el muelle (GlobalEmailComposer.vue), hasta MAX_COMPOSER_SESSIONS en total.
 * `host`/`window` gobiernan dónde se ve y cómo; el resto es el estado que
 * antes vivía en refs sueltos del store (un solo compositor a la vez).
 */
export interface ComposerSession {
  id: number;
  mode: ComposerMode;
  emailId: string | null;
  draft: EmailDraft | null;
  contextModule: 'shipment' | 'purchaseOrder' | null;
  contextEntityId: string | null;
  contextEntityIdNumber: string | null;
  initialTemplate: EmailTemplate | null;
  openTemplatesOnMount: boolean;
  templateContext: TemplateDataContext | null;
  attachmentSource: AttachmentSource | null;
  initialSubject: string | null;
  /** Destinatarios/cuerpo con los que arranca un correo nuevo (respuesta a un embebido, ver embeddedEmailReply.ts) */
  initialTo: EmailAddress[] | null;
  initialCc: EmailAddress[] | null;
  initialBodyHtml: string | null;
  threadHeaders: { inReplyTo?: string; references?: string } | null;
  /** Clave de dedup para sesiones sin emailId (por ejemplo, una respuesta a un correo adjunto) */
  dedupeKey: string | null;
  host: ComposerHost;
  /** Solo aplica a host 'floating' */
  window: ComposerWindowState;
  lastFocusedAt: number;
  isSending: boolean;
  pendingFiles: File[];
  pendingAttachedEmails: AttachedEmailRef[];
}

export interface ComposerOpenExtras {
  initialTemplate?: EmailTemplate | null;
  openTemplatesOnMount?: boolean;
  templateContext?: TemplateDataContext;
  attachmentSource?: AttachmentSource;
  attachments?: File[];
  attachedEmails?: AttachedEmailRef[];
  initialSubject?: string;
  initialTo?: EmailAddress[];
  initialCc?: EmailAddress[];
  initialBodyHtml?: string;
  threadHeaders?: { inReplyTo?: string; references?: string };
  /** Clave de dedup para sesiones sin emailId, por ejemplo una respuesta a un correo adjunto */
  dedupeKey?: string;
  /** default 'floating' */
  host?: ComposerHost;
}

/** Familia de sesión para deduplicar: responder y responder a todos comparten hilo con reenviar. */
function sessionFamily(mode: ComposerMode): 'reply' | 'forward' {
  return mode === 'forward' ? 'forward' : 'reply';
}

export const useUiStore = defineStore('ui', () => {
  const notifications = ref<NotificationMessage[]>([]);
  const isSidebarOpen = ref(true);
  const isSidebarCollapsed = ref(localStorage.getItem('emailSidebarCollapsed') === 'true');

  // Anchos ajustables de los paneles del módulo de correo (persistidos)
  const emailSidebarWidth = ref(Number(localStorage.getItem('emailSidebarWidth')) || 256);
  const emailListWidth = ref(Number(localStorage.getItem('emailListWidth')) || 384);
  const emailAssocWidth = ref(Number(localStorage.getItem('emailAssocWidth')) || 320);

  // Sesiones de compositor: la bandeja conserva la suya en línea; el resto
  // vive en el muelle flotante (GlobalEmailComposer.vue), apiladas de más
  // nueva a más vieja.
  const composerSessions = ref<ComposerSession[]>([]);
  const activeComposerSessionId = ref<number | null>(null);
  const inlineComposerTarget = shallowRef<HTMLElement | null>(null);
  /** Se incrementa con cada envío exitoso; lo observan las listas de correos asociados para recargar. */
  const composerSentVersion = ref(0);
  let nextSessionId = 1;

  const isComposerOpen = computed(() => composerSessions.value.length > 0);
  const inlineSessionId = computed(() => composerSessions.value.find(s => s.host === 'inline')?.id ?? null);

  function getComposerSession(id: number): ComposerSession | undefined {
    return composerSessions.value.find(s => s.id === id);
  }

  /**
   * Minimiza toda ventana flotante que no sea `id` y no esté ya minimizada, y
   * deja `id` como la única abierta (o maximizada). Nunca hay más de una
   * ventana flotante abierta ni más de una maximizada.
   */
  function focusSession(id: number) {
    const session = getComposerSession(id);
    if (!session) return;

    // Una sesión en línea no minimiza a las flotantes: solo la bandeja tiene
    // esa; el muelle sigue como estaba.
    if (session.host === 'floating') {
      for (const other of composerSessions.value) {
        if (other.id === id) continue;
        if (other.host === 'floating' && other.window !== 'minimized') {
          other.window = 'minimized';
        }
      }

      if (session.window === 'minimized') session.window = 'open';
      // 'maximized' se deja como está
    }

    session.lastFocusedAt = Date.now();
    activeComposerSessionId.value = id;
  }

  function openComposer(
    mode: ComposerMode = 'new',
    emailId?: string,
    contextModule?: 'shipment' | 'purchaseOrder',
    contextEntityId?: string,
    contextEntityIdNumber?: string,
    draft?: EmailDraft | null,
    extras: ComposerOpenExtras = {}
  ): number | null {
    // Deduplicar: mismo borrador, misma familia (reply/forward) del mismo
    // correo, o la clave explícita que manda el llamador (por ejemplo, una
    // respuesta a un correo adjunto sin emailId propio).
    const dedupKey = draft?.id
      ? `draft:${draft.id}`
      : mode !== 'new' && emailId
        ? `${sessionFamily(mode)}:${emailId}`
        : extras.dedupeKey ?? null;

    if (dedupKey) {
      const existing = composerSessions.value.find(s => {
        const key = s.draft?.id
          ? `draft:${s.draft.id}`
          : s.mode !== 'new' && s.emailId
            ? `${sessionFamily(s.mode)}:${s.emailId}`
            : s.dedupeKey ?? null;
        return key === dedupKey;
      });

      if (existing) {
        if (extras.attachments?.length) queueSessionFiles(existing.id, extras.attachments);
        if (extras.attachedEmails?.length) queueSessionAttachedEmails(existing.id, extras.attachedEmails);
        focusSession(existing.id);
        return existing.id;
      }
    }

    if (composerSessions.value.length >= MAX_COMPOSER_SESSIONS) {
      useNotification().showNotification(
        'warning',
        i18n.global.t('emailComposer.notifications.tooManyComposersTitle'),
        i18n.global.t('emailComposer.notifications.tooManyComposers', { max: MAX_COMPOSER_SESSIONS })
      );
      return null;
    }

    const host: ComposerHost = extras.host === 'inline' && inlineComposerTarget.value ? 'inline' : 'floating';
    if (host === 'inline') {
      const existingInline = composerSessions.value.find(s => s.host === 'inline');
      if (existingInline) {
        existingInline.host = 'floating';
        existingInline.window = 'minimized';
      }
    }

    const session: ComposerSession = {
      id: nextSessionId++,
      mode,
      emailId: emailId || null,
      draft: draft ? markRaw({ ...draft }) : null,
      contextModule: contextModule || null,
      contextEntityId: contextEntityId || null,
      contextEntityIdNumber: contextEntityIdNumber || null,
      initialTemplate: extras.initialTemplate ? markRaw({ ...extras.initialTemplate }) : null,
      openTemplatesOnMount: extras.openTemplatesOnMount || false,
      templateContext: extras.templateContext ? markRaw({ ...extras.templateContext }) : null,
      attachmentSource: extras.attachmentSource ? markRaw({ ...extras.attachmentSource }) : null,
      initialSubject: extras.initialSubject || null,
      initialTo: extras.initialTo ? [...extras.initialTo] : null,
      initialCc: extras.initialCc ? [...extras.initialCc] : null,
      initialBodyHtml: extras.initialBodyHtml || null,
      threadHeaders: extras.threadHeaders || null,
      dedupeKey: extras.dedupeKey || null,
      host,
      window: 'open',
      lastFocusedAt: Date.now(),
      isSending: false,
      pendingFiles: [...(extras.attachments || [])],
      pendingAttachedEmails: [...(extras.attachedEmails || [])],
    };

    composerSessions.value.unshift(session);
    focusSession(session.id);
    return session.id;
  }

  function closeComposer(sessionId: number) {
    const index = composerSessions.value.findIndex(s => s.id === sessionId);
    if (index === -1) return;
    composerSessions.value.splice(index, 1);
    if (activeComposerSessionId.value === sessionId) activeComposerSessionId.value = null;
  }

  function setSessionWindow(id: number, state: ComposerWindowState) {
    if (state === 'minimized') {
      const session = getComposerSession(id);
      if (!session) return;
      session.window = 'minimized';
      if (activeComposerSessionId.value === id) activeComposerSessionId.value = null;
      return;
    }

    // 'open' y 'maximized' pasan por la misma regla que enfocar.
    focusSession(id);
    const session = getComposerSession(id);
    if (session) session.window = state;
  }

  function touchSession(id: number) {
    const session = getComposerSession(id);
    if (!session) return;
    session.lastFocusedAt = Date.now();
    activeComposerSessionId.value = id;
  }

  function popOutSession(id: number) {
    const session = getComposerSession(id);
    if (!session) return;
    session.host = 'floating';
    focusSession(id);
  }

  function setInlineComposerTarget(el: HTMLElement | null) {
    inlineComposerTarget.value = el;
    if (el === null && inlineSessionId.value !== null) {
      popOutSession(inlineSessionId.value);
    }
  }

  function setSessionEntityContext(
    id: number,
    templateContext?: TemplateDataContext,
    attachmentSource?: AttachmentSource
  ) {
    const session = getComposerSession(id);
    if (!session) return;
    if (templateContext !== undefined) session.templateContext = markRaw({ ...templateContext });
    if (attachmentSource !== undefined) session.attachmentSource = markRaw({ ...attachmentSource });
  }

  function queueSessionFiles(id: number, files: File[]) {
    const session = getComposerSession(id);
    if (!session) return;
    session.pendingFiles.push(...files);
  }

  function queueSessionAttachedEmails(id: number, refs: AttachedEmailRef[]) {
    const session = getComposerSession(id);
    if (!session) return;
    session.pendingAttachedEmails.push(...refs);
  }

  function takeSessionQueues(id: number): { files: File[]; attachedEmails: AttachedEmailRef[] } {
    const session = getComposerSession(id);
    if (!session) return { files: [], attachedEmails: [] };
    const files = session.pendingFiles;
    const attachedEmails = session.pendingAttachedEmails;
    session.pendingFiles = [];
    session.pendingAttachedEmails = [];
    return { files, attachedEmails };
  }

  function setSessionSending(id: number, sending: boolean) {
    const session = getComposerSession(id);
    if (!session) return;
    session.isSending = sending;
  }

  /**
   * A qué sesión se suma "Reenviar como adjunto": la última enfocada entre
   * las visibles (en línea, o flotante no minimizada); si todas están
   * minimizadas, la última usada. Nunca una que está enviando.
   */
  function targetSessionForAttachment(): ComposerSession | null {
    const candidates = composerSessions.value.filter(s => !s.isSending);
    if (candidates.length === 0) return null;

    const visible = candidates.filter(s => s.host === 'inline' || s.window !== 'minimized');
    const pool = visible.length > 0 ? visible : candidates;

    return [...pool].sort((a, b) => b.lastFocusedAt - a.lastFocusedAt)[0];
  }

  function markComposerSent() {
    composerSentVersion.value++;
  }

  function addNotification(
    type: NotificationMessage['type'],
    title: string,
    message: string,
    duration: number = 5000
  ) {
    const notification: NotificationMessage = {
      id: Date.now().toString(),
      type,
      title,
      message,
      duration,
      timestamp: new Date().toISOString(),
    };

    notifications.value.push(notification);

    if (duration > 0) {
      setTimeout(() => {
        removeNotification(notification.id);
      }, duration);
    }
  }

  function removeNotification(id: string) {
    notifications.value = notifications.value.filter(n => n.id !== id);
  }

  function clearNotifications() {
    notifications.value = [];
  }

  function toggleSidebar() {
    isSidebarOpen.value = !isSidebarOpen.value;
  }

  function toggleSidebarCollapse() {
    isSidebarCollapsed.value = !isSidebarCollapsed.value;
    localStorage.setItem('emailSidebarCollapsed', String(isSidebarCollapsed.value));
  }

  function setEmailSidebarWidth(width: number) {
    emailSidebarWidth.value = width;
    localStorage.setItem('emailSidebarWidth', String(width));
  }

  function setEmailListWidth(width: number) {
    emailListWidth.value = width;
    localStorage.setItem('emailListWidth', String(width));
  }

  function setEmailAssocWidth(width: number) {
    emailAssocWidth.value = width;
    localStorage.setItem('emailAssocWidth', String(width));
  }

  function showSuccess(title: string, message: string) {
    addNotification('success', title, message);
  }

  function showError(title: string, message: string) {
    addNotification('error', title, message, 7000);
  }

  function showWarning(title: string, message: string) {
    addNotification('warning', title, message);
  }

  function showInfo(title: string, message: string) {
    addNotification('info', title, message);
  }

  function showEmailNotification(sender: string, subject: string) {
    addNotification('email', sender, subject, 5000);
  }

  return {
    notifications,
    isComposerOpen,
    isSidebarOpen,
    isSidebarCollapsed,
    emailSidebarWidth,
    emailListWidth,
    emailAssocWidth,
    composerSessions,
    activeComposerSessionId,
    inlineComposerTarget,
    composerSentVersion,
    inlineSessionId,
    getComposerSession,
    addNotification,
    removeNotification,
    clearNotifications,
    openComposer,
    closeComposer,
    focusSession,
    setSessionWindow,
    touchSession,
    popOutSession,
    setInlineComposerTarget,
    setSessionEntityContext,
    queueSessionFiles,
    queueSessionAttachedEmails,
    takeSessionQueues,
    setSessionSending,
    targetSessionForAttachment,
    markComposerSent,
    toggleSidebar,
    toggleSidebarCollapse,
    setEmailSidebarWidth,
    setEmailListWidth,
    setEmailAssocWidth,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showEmailNotification,
  };
});
